/**
 * SYM-6194. Adding a slide by hand.
 *
 * A slide added in the wizard used to be invented in the browser: a made-up
 * position number and a placeholder photograph fetched from picsum.photos. It
 * named no picture the customer owns, so nothing survived the trip to the
 * server. What is sent per slide is the position number, plus a picture
 * reference only when one exists (`updatePayload.ts`), and the server matches
 * an arriving slide to a stored one by that number, falling back to place in
 * the list and otherwise to an empty slide
 * (`VideoGenerationController::resolveStoredSlideForPatch`). A slide with
 * neither resolved to nothing, and the video came back with the original
 * pictures.
 *
 * So a slide is only created once the customer has chosen a real picture for
 * it, and it carries that picture's id.
 *
 * Two numbers matter here and they are not the same thing:
 *
 * - the slide's POSITION in the list, which is the order the video plays in;
 * - the slide's `index`, which is the picture slot the server matches on and
 *   is deliberately left alone when slides are reordered or removed.
 *
 * A new slide therefore takes an `index` no existing slide is using, so it
 * cannot be mistaken for one of them.
 */

import { CustomerResource } from 'types/apiTypes';

import { paceFor, StylePaces } from './stylePace';
import { VideoScene, VideoTemplateStyle } from './types';

/**
 * The most slides a Track 1 video keeps.
 *
 * This mirrors the server's cap, `slide_caps_by_provider.creatomate` in
 * `config/video-generation.php`, which defaults to 4 and can be moved with
 * `VIDEO_SLIDE_CAP_CREATOMATE`. Slides past it are dropped on the way through
 * and only ever recorded in a log, so the customer is better stopped here,
 * where a reason can be given.
 */
export const MAX_SLIDES = 4;

/** Whether another slide can be added to a video that currently has this many. */
export function canAddSlide(slideCount: number): boolean {
  return slideCount < MAX_SLIDES;
}

/** The picture slot to give a new slide: one past the highest in use. */
export function nextFreeIndex(scenes: VideoScene[]): number {
  return scenes.reduce(
    (highest, scene) =>
      typeof scene.index === 'number' && scene.index >= highest
        ? scene.index + 1
        : highest,
    0
  );
}

/**
 * The length a slide gets when the chosen look stands for no pace at all.
 *
 * This is the number the wizard has always used. It is a last resort, not a
 * default anyone chose: every look the server knows carries its own length.
 */
export const FALLBACK_SLIDE_SECONDS = 3;

/**
 * SYM-6299. How long a newly added slide should run: the length the chosen
 * look stands for, which is 3.2 s on Dynamic, 3.6 s on Professional and 4.0 s
 * on Minimal.
 *
 * Until now every new slide was born at 3 s whatever the look, so a fourth
 * slide added to a Dynamic video came in shorter than the three beside it and
 * the total read 10.8 s where the customer expected 11 s. Reported by hand on
 * 21 September 2026. Picking a different look afterwards did fix it, because
 * that puts the look's pace on every slide -- which is the behaviour this
 * borrows from (`applyStylePace`).
 */
export function newSlideDuration(
  style: VideoTemplateStyle,
  paces: StylePaces | null | undefined
): number {
  return paceFor(style, paces) ?? FALLBACK_SLIDE_SECONDS;
}

/**
 * A new slide built around a picture the customer picked, ready to be added at
 * the end of the list.
 *
 * SYM-6299. `duration` is the look's own pace, and the slide is deliberately
 * NOT marked as set by hand: the customer did not choose this length, the look
 * did, so a later change of look must keep moving it along with the rest.
 */
export function newSceneFromResource(
  resource: CustomerResource,
  index: number,
  duration: number = FALLBACK_SLIDE_SECONDS
): VideoScene {
  return {
    id: `scene-new-${Date.now()}`,
    index,
    headline: '',
    subtext: '',
    imageUrl: resource.path,
    resourceId: resource.id,
    imageCrop: null,
    duration,
    transition: 'fade',
  };
}
